import ldb from 'localdata';
import lzjs from 'lzjs';
import type { IAtom, ObservableMap } from 'mobx';
import { action, computed, createAtom, observable, runInAction } from 'mobx';
import { createTransformer } from 'mobx-utils';
import type { CacheResponse } from 'tm3-core/api.dtos';
import { v4 as uuid } from 'uuid';

class CachedMapStore<T> {
  protected atom: IAtom;
  protected key: string;
  public orderBy?: keyof T;
  public version: string | undefined;
  public versionValidated: boolean = false;

  @observable loaded = false;
  @observable _all: ObservableMap<string, T> = observable.map<string, T>(new Map());

  public readonly getById = createTransformer<number | string, T>((id) => {
    if (!id) {
      return null;
    }
    return this.all.get(id.toString()) || null;
  });

  constructor(key: string) {
    this.atom = createAtom(key);
    this.key = key;
    this.version = localStorage.getItem(`cache:${this.key}:version`) || uuid();
    addEventListener('storage', async () => await this.storageInOtherTabChanged);
    ldb.get(`cache:${this.key}:version`, (v: string) => {
      if (v !== this.version) this.version = uuid();
      this.versionValidated = true;
    });
  }

  @computed
  get All(): T[] {
    const res = Array.from(this.all.values()) || [];
    const { orderBy } = this;
    try {
      if (!!orderBy) {
        res.sort((a, b) => {
          if (a[orderBy] == null || b[orderBy] == null) {
            return 0;
          }
          if (typeof a[orderBy] === 'number') {
            return a[orderBy] - b[orderBy];
          }
          return a[orderBy].localeCompare(b[orderBy]);
        });
      }
    } catch {
      // no ordering is going to happen
      console.error('A problem arose when attempting to order these items');
    }

    return res;
  }

  protected get all(): Map<string, T> {
    this.atom.reportObserved();
    return this._all;
  }

  @action.bound
  public async onLoad(
    key: string,
    cacheResponse: CacheResponse,
    sortBy: string = ''
  ): Promise<void> {
    const version = cacheResponse.keys?.[key];
    const req = cacheResponse[key] as Record<string, T> | null | undefined;

    // Not a request for this cache
    if (!version && !req && this.loaded) {
      return;
    }

    // Version or user change
    if ((version && version !== this.version) || req) {
      const nextVersion = version ?? this.version ?? uuid();
      const map = new Map<string, T>(Object.entries(req ?? {}));
      this.version = nextVersion;
      await this.saveToLocalStorage(nextVersion, map);
    }

    const loaded = await this.loadFromLocalStorage();
    this._all.replace(loaded);
    this.atom.reportChanged();
  }

  @action.bound
  public onClear(): void {
    this.loaded = false;
    this._all.clear();
    this.atom.reportChanged();
    this.clearLocalStorage();
  }

  @action.bound
  public onCreate(id: string | number, value: T): void {
    if (typeof id === 'number') {
      return this._onCreate(id.toString(), value);
    }
    this._onCreate(id, value);
    this.atom.reportChanged();
  }

  @action.bound
  public onUpdate(id: string | number, value: T): void {
    if (typeof id === 'number') {
      return this._onUpdate(id.toString(), value);
    }
    this._onUpdate(id, value);
    this.atom.reportChanged();
  }

  @action.bound
  public onDelete(id: string | number): void {
    if (typeof id === 'number') {
      return this._onDelete(id.toString());
    }
    this._onDelete(id);
    this.atom.reportChanged();
  }

  protected async saveToLocalStorage(cacheKey: string, map: Map<string, T>): Promise<void> {
    try {
      await new Promise<void>((resolve) => {
        ldb.set(`cache:${this.key}`, lzjs.compress(JSON.stringify([...map])), resolve);
      });

      // set the version second, so that it won't be there if the storage quota errors
      localStorage.setItem(`cache:${this.key}:version`, cacheKey);
      ldb.set(`cache:${this.key}:version`, cacheKey, () => {});
    } catch (err) {
      console.log(`Error saving to local storage cache:${this.key}`, err);
      // revert in case of storage quota exceeded.
      this._all.replace(map);
    }
  }

  protected async loadFromLocalStorage(): Promise<Map<string, T>> {
    try {
      return await new Promise<Map<string, T>>((resolve, reject) => {
        ldb.get(`cache:${this.key}`, (value: string) => {
          try {
            if (!value) throw new Error('Null entry');
            const obj = JSON.parse(lzjs.decompress(value));
            if (!!obj) {
              resolve(new Map(obj));
            }
          } catch (err) {
            reject(err);
          }
        });
      });
    } catch (e) {
      console.error(
        `[CacheMapStore.loadFromLocalStorage] - Error loading data for :${this.key}`,
        e
      );
      return new Map();
    } finally {
      runInAction(() => {
        this.loaded = true;
      });
    }
  }

  protected clearLocalStorage() {
    ldb.delete(`cache:${this.key}`);
    localStorage.removeItem(`cache:${this.key}:version`);
    this.version = uuid();
  }

  @action.bound
  private _onCreate(id: string, value: T): void {
    if (!id) {
      return;
    }

    const { all } = this;
    if (all.has(id)) {
      return this._onUpdate(id, value);
    }

    this.all.set(id, value);
    this.saveToLocalStorage(uuid(), this.all);
  }

  @action.bound
  private _onUpdate(id: string, value: T): void {
    if (!id) {
      return;
    }

    const cached = this.all.get(id) as T;
    this.all.set(id, value);
    this.saveToLocalStorage(uuid(), this.all);
  }

  @action.bound
  private _onDelete(id: string): void {
    if (!id) {
      return;
    }
    this.all.delete(id);
    this.saveToLocalStorage(uuid(), this.all);
  }

  @action.bound
  private storageInOtherTabChanged(e: StorageEvent) {
    if (e.key === `cache:${this.key}:version`) {
      this.version = e.newValue!;
      this.loadFromLocalStorage();
    }
  }
}

export default CachedMapStore;
